/**
 * Event naming utilities — single source of truth for canonical event name format.
 *
 * Convention: `entityType.verb` or `entityType.subResource.verb`.
 * - entityType: camelCase singular noun (e.g. `risk`, `riskRegister`, `changeRequest`).
 * - verb: camelCase past-tense or state (`created`, `updated`, `deleted`, `linked`, `submitted`).
 * - sub-resource (optional): camelCase singular (`document`, `member`, `instance`).
 *
 * Legacy strings using PascalCase entity (`Asset.created`), kebab-case
 * (`change-request.created`) or snake_case (`document.pending_approval`) are
 * normalized through `toCanonicalEventName` so they match the catalog without
 * touching emit sites in one shot.
 */

const SEGMENT_RE = /^[a-z][a-zA-Z0-9]*$/;

export function canonicalEventPrefix(prefix) {
    return toCamelCase(String(prefix ?? '').trim());
}

/**
 * Normalize any case (kebab-case, PascalCase, snake_case, camelCase) to camelCase.
 * Examples:
 *   'change-request' -> 'changeRequest'
 *   'BcmInitialResponseLibrary' -> 'bcmInitialResponseLibrary'
 *   'pending_approval' -> 'pendingApproval'
 *   'Risk' -> 'risk'
 */
export function toCamelCase(value) {
    if (value == null) return '';
    const raw = String(value).trim();
    if (!raw) return '';
    if (SEGMENT_RE.test(raw)) return raw;
    const parts = raw
        .replace(/[\s_\-]+/g, '-')
        .split('-')
        .filter(Boolean);
    if (parts.length === 0) return '';
    const first = parts[0].charAt(0).toLowerCase() + parts[0].slice(1);
    const rest = parts.slice(1).map((p) => p.charAt(0).toUpperCase() + p.slice(1));
    return [first, ...rest].join('').replace(/^([A-Z]+)/, (m) => m.toLowerCase());
}

/**
 * Build a canonical event name from parts.
 * `toEventName('risk', 'created')` => 'risk.created'.
 * `toEventName('bcp', 'linked', 'document')` => 'bcp.document.linked'.
 */
export function toEventName(entity, verb, subResource) {
    const e = toCamelCase(entity);
    const v = toCamelCase(verb);
    if (!e || !v) throw new Error(`[events] toEventName: invalid parts entity="${entity}" verb="${verb}"`);
    if (subResource) {
        const s = toCamelCase(subResource);
        return `${e}.${s}.${v}`;
    }
    return `${e}.${v}`;
}

/**
 * Parse an event name into structured parts.
 * Accepts canonical names AND legacy variants (PascalCase / kebab-case / snake_case).
 * Returns null when the input is not a string with at least one dot.
 *
 * Returns: { entity, verb, subResource: string|null, raw: string, canonical: string }
 */
export function parseEventName(name) {
    if (!name || typeof name !== 'string') return null;
    const idx = name.lastIndexOf('.');
    if (idx <= 0) return null;
    const verbRaw = name.slice(idx + 1);
    const prefix = name.slice(0, idx);
    if (!verbRaw) return null;
    const segments = prefix.split('.');
    const entityRaw = segments[0];
    const subRaw = segments.length > 1 ? segments.slice(1).join('.') : null;

    const entity = canonicalEventPrefix(entityRaw);
    const verb = toCamelCase(verbRaw);
    const subResource = subRaw ? subRaw.split('.').map(canonicalEventPrefix).join('.') : null;
    if (!entity || !verb) return null;

    const canonical = subResource ? `${entity}.${subResource}.${verb}` : `${entity}.${verb}`;
    return { entity, verb, subResource, raw: name, canonical };
}

/**
 * Normalize a possibly-legacy event name to canonical form.
 * Returns the canonical string or the original input if it cannot be parsed.
 */
export function toCanonicalEventName(name) {
    const parsed = parseEventName(name);
    return parsed ? parsed.canonical : name;
}

/**
 * Compare two event names ignoring case style differences.
 * `eventNamesEqual('change-request.created', 'changeRequest.created')` => true
 */
export function eventNamesEqual(a, b) {
    return toCanonicalEventName(a) === toCanonicalEventName(b);
}

/**
 * Compare workflow TRIGGER `config.event` verbs ignoring kebab vs camelCase.
 * `review-due` and `reviewDue` are equivalent; `created` matches `created`.
 */
export function triggerVerbsEqual(a, b) {
    if (a == null || b == null) return false;
    return toCamelCase(a) === toCamelCase(b);
}

/**
 * Quick validity check (no catalog lookup; catalog is enforced separately).
 */
export function isWellFormedEventName(name) {
    return parseEventName(name) !== null;
}
